import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Download, Save, Clock } from "lucide-react";
import { api, getToken, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "Date à préciser";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

const TECHNICAL_RE = /^(test[_\s-]|iter\d+|fixture|demo[_\s-]|debug|__)/i;
function cleanName(raw) {
  if (!raw) return "Votre mariage";
  let s = String(raw).replace(/^\s*(test[_\s-]+|demo[_\s-]+)/i, "").trim();
  s = s.replace(/\s+[a-f0-9]{6,}$/i, "").trim();
  return TECHNICAL_RE.test(s) ? "Votre mariage" : s;
}

export default function MyEventPlanning() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    api.get(`/next/myevent/${eventId}/planning`)
      .then(r => {
        setEvent(r.data.event);
        setItems(r.data.items || []);
      })
      .catch(e => setErr(e?.response?.status || 500))
      .finally(() => setLoading(false));
  }, [eventId]);

  if (!user) return <Navigate to="/connexion" replace />;
  if (err === 403) return <ErrorScreen title="Accès refusé" text="Vous n'êtes pas membre de cet événement." testid="myplanning-forbidden" />;
  if (err === 404) return <ErrorScreen title="Événement introuvable" text="Cet événement n'existe pas." testid="myplanning-notfound" />;
  if (err) return <ErrorScreen title="Erreur" text="Impossible de charger le planning." testid="myplanning-error" />;

  const add = () => setItems(it => [...it, { heure: "", label: "", notes: "" }]);
  const rm = (i) => setItems(it => it.filter((_, idx) => idx !== i));
  const update = (i, k, v) => setItems(it => it.map((row, idx) => idx === i ? { ...row, [k]: v } : row));

  const sortedItems = [...items].sort((a, b) =>
    (a.heure || "").localeCompare(b.heure || "")
  );

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/next/myevent/${eventId}/planning`, { items });
      setItems(r.data.items || []);
      toast.success("Planning enregistré.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    try {
      const token = getToken();
      const r = await fetch(`${API}/next/myevent/${eventId}/planning/pdf`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Impossible de générer le PDF.");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "myplanning-my-animation.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Planning téléchargé.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const coupleName = cleanName(event?.name);

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="myevent-planning">
      {/* Header simple */}
      <header className="border-b border-[#DCB457]/15 bg-black/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-3">
          <Link to={`/myevent/${eventId}`} className="text-xs uppercase tracking-widest text-white/60 hover:text-[#DCB457] inline-flex items-center gap-2" data-testid="myplanning-back">
            <ArrowLeft size={13} /> MyEvent
          </Link>
          <span className="text-[10px] uppercase tracking-[0.24em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1">MyPlanning</span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 md:px-10 pt-10 md:pt-14">
        {/* Titre */}
        <section className="mb-10">
          <p className="eyebrow">MyEvent · Module</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Votre <span className="italic gold-text">planning.</span></h1>
          <p className="mt-4 text-white/65 text-base leading-relaxed">Organisez chaque étape de votre mariage, heure par heure.</p>
          <p className="mt-2 text-[#DCB457] text-sm" data-testid="myplanning-event-summary">
            {coupleName} — {fmtDate(event?.date)}{event?.lieu ? ` · ${event.lieu}` : ""}
          </p>
        </section>

        {/* Actions */}
        <div className="flex items-center gap-3 flex-wrap mb-6">
          <button onClick={add} data-testid="myplanning-add" className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 text-xs uppercase tracking-widest">
            <Plus size={13} /> Ajouter une étape
          </button>
          <button onClick={save} disabled={saving} data-testid="myplanning-save" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#DCB457] text-black hover:bg-[#DCB457]/90 text-xs uppercase tracking-widest disabled:opacity-50">
            <Save size={13} /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
          <button onClick={downloadPdf} data-testid="myplanning-pdf" className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 text-white/70 hover:text-[#DCB457] hover:border-[#DCB457]/40 text-xs uppercase tracking-widest">
            <Download size={13} /> Télécharger en PDF
          </button>
        </div>

        {/* Liste chronologique — mobile first */}
        <section className="card-luxe p-4 md:p-6" data-testid="myplanning-list">
          {loading ? (
            <p className="text-white/50 italic text-sm">Chargement…</p>
          ) : sortedItems.length === 0 ? (
            <p className="text-white/50 italic text-sm">Aucune étape pour l'instant. Cliquez sur « Ajouter une étape » pour commencer.</p>
          ) : (
            <ul className="space-y-3">
              {sortedItems.map((row, i) => {
                // Find the ORIGINAL index in items (to update the right row)
                const originalIdx = items.indexOf(row);
                return (
                  <li key={originalIdx} className="grid grid-cols-12 gap-2 md:gap-3 items-start bg-black/40 border border-[#DCB457]/15 p-3" data-testid={`myplanning-row-${originalIdx}`}>
                    <div className="col-span-4 md:col-span-2 relative">
                      <Clock size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#DCB457]/60" />
                      <input type="time" value={row.heure || ""}
                        onChange={(e) => update(originalIdx, "heure", e.target.value)}
                        data-testid={`myplanning-heure-${originalIdx}`}
                        className="w-full bg-black/60 border border-[#DCB457]/25 pl-7 pr-2 py-2 text-sm text-white focus:border-[#DCB457] focus:outline-none" />
                    </div>
                    <input type="text" value={row.label || ""} placeholder="ex. Cocktail / Entrée des mariés"
                      onChange={(e) => update(originalIdx, "label", e.target.value)}
                      data-testid={`myplanning-label-${originalIdx}`}
                      className="col-span-8 md:col-span-5 bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm text-white focus:border-[#DCB457] focus:outline-none" />
                    <input type="text" value={row.notes || ""} placeholder="Notes (facultatif)"
                      onChange={(e) => update(originalIdx, "notes", e.target.value)}
                      data-testid={`myplanning-notes-${originalIdx}`}
                      className="col-span-11 md:col-span-4 bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm text-white/80 focus:border-[#DCB457] focus:outline-none" />
                    <button type="button" onClick={() => rm(originalIdx)}
                      data-testid={`myplanning-remove-${originalIdx}`}
                      className="col-span-1 flex items-center justify-center text-red-300/70 hover:text-red-300 h-full">
                      <Trash2 size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <p className="text-[10px] uppercase tracking-widest text-white/25 text-center mt-16">
          MyEvent · MyPlanning v1
        </p>
      </div>
    </div>
  );
}

function ErrorScreen({ title, text, testid }) {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
      <div className="max-w-md text-center" data-testid={testid}>
        <p className="eyebrow">MyPlanning</p>
        <h1 className="font-serif-luxe text-3xl mt-3 mb-3">{title.split(" ")[0]}{" "}
          <span className="italic gold-text">{title.split(" ").slice(1).join(" ") || "."}</span>
        </h1>
        <p className="text-white/60 text-sm">{text}</p>
      </div>
    </div>
  );
}
